/* eslint-disable max-len */
import * as images from '../../components/assets/Images';
import * as icons from '../../components/assets/images/Icons';
import * as logos from '../../components/assets/images/Logos';
import { AuthLayoutConfig } from '../../components/flows/layouts/AuthLayout';
import { getAuthMethodUsed, getPaymentRoute } from '../../util/ConditionalNextScreenSelectors';
import { NoMenopausal } from '../AssessmentUtils';
import { FlowConfig, ScreenType, SharedScreenName } from '../FlowTypes';

const authLayout: AuthLayoutConfig = {
  registerTitle: 'One last step,\n so we can finalize your results!',
  registerBody: ['Complete your profile to find out whether Evia could work for you.'],
  registerPrivacyDeclaration: [
    'Evia will not sell any of your personal information. Ever. That’s a promise.',
  ],
  loginTitle: 'Welcome back!',
  registerButtonTitle: 'See my results',
  loginButtonTitle: 'See my program',
};

export enum MenoScreenName {
  MenopauseHormoneLevel = 'menopauseHormoneLevel',
  BrainSafeRange = 'brainSafeRange',
  BrainPhysicalReaction = 'brainPhysicalReaction',
  HealthIssues = 'healthIssues',
  Hypnothalamus = 'hypnothalamus',
  HypnotherapySleep = 'hypnotherapySleep',
  EviaSocialProof = 'eviaSocialProof',
  ToolsIneffectivenessReason = 'toolsIneffectivenessReason',
  HotFlashManagement = 'hotFlashManagement',
  HypnotherapyMentalHealth = 'hypnotherapyMentalHealth',
  HypnotherapyTried = 'hypnotherapyTried',
  HypnotherapyEstablishedTechnique = 'hypnotherapyEstablishedTechnique',
  DrElkins = 'drElkins',
  UserInfoRecap = 'userInfoRecap',
  ProgramIntent = 'programIntent',
  NotMenopausal = 'notMenopausal',
  HotFlash = 'hotFlash',
  SleepDifficulty = 'sleepDifficulty',
  HotFlashIntensity = 'hotFlashIntensity',
  HotFlashSymptoms = 'hotFlashSymptoms',
  NightSweatsHowMany = 'nightSweatsHowMany',
  SleepDisruption = 'sleepDisruption',
  SleepQuality = 'sleepQuality',
  Anxiety = 'anxiety',
  BrainAdapt = 'brainAdapt',
  StruggleWork = 'struggleWork',
  StruggleRelationship = 'struggleRelationship',
  ChangeHrt = 'changeHrt',
  HrtSideEffects = 'hrtSideEffects',
  NaturalToolsTried = 'naturalToolsTried',
  NaturalToolsEfectiveness = 'naturalToolsEfectiveness',
  Hormones = 'hormones',
  NonHormonalTool = 'nonHormonalTool',
  EviaHypnotherapy = 'eviaHypnotherapy',
  HotFlashesHypnotherapy = 'hotFlashesHypnotherapy',
}

export type MenoAssessmentScreen = SharedScreenName | MenoScreenName;

const NEXT_BUTTON_TITLE = 'Next';

/**
 * Config for full Meno flow.
 *
 * This should include all web-onboarding screens (assessment, education, payment, etc.) for Meno.
 */
export const MENO_CONFIG: FlowConfig<MenoAssessmentScreen> = {
  name: 'MenoConfig',
  showProgressBar: true,
  startEvent: undefined,
  screens: [
    {
      screenName: MenoScreenName.ProgramIntent,
      type: ScreenType.MultiSelect,
      layout: {
        body: [
          'We’ll ask some questions to assess whether Evia is likely to work for you.',
          'Which symptoms would you like to manage?',
          'Select all that apply.',
        ],
        primaryButtonTitle: NEXT_BUTTON_TITLE,
        randomizeChoices: true,
        choices: ['Hot flushes', 'Night sweats', 'Insomnia', 'Anxiety', 'Depression'],
      },
      nextScreen: SharedScreenName.Age,
    },
    {
      screenName: SharedScreenName.Age,
      type: ScreenType.SingleInput,
      layout: {
        type: 'number',
        collectsPIIData: false,
        content: 'What’s your age?',
        primaryButtonTitle: NEXT_BUTTON_TITLE,
        inputLabel: 'Age',
        inputPlaceholder: 'Age',
        characterLimit: 3,
        minimumValue: 18,
      },
      nextScreen: SharedScreenName.HealthIssues,
    },
    {
      screenName: SharedScreenName.HealthIssues,
      type: ScreenType.MultiSelect,
      layout: {
        body: [
          'Do you have any other health issues?',
          'It’s common for people with Menopause to have multiple related conditions.',
        ],
        primaryButtonTitle: NEXT_BUTTON_TITLE,
        choices: [
          'Anxiety/Stress',
          'Depression',
          'Smoking',
          'IBS',
          'Chronic pain',
          'Sleep problems',
          'Skin Issues',
          'Obesity',
          'More/Other',
          'None',
        ],
      },
      nextScreen: SharedScreenName.Classify,
    },
    {
      screenName: SharedScreenName.Classify,
      type: ScreenType.SingleSelect,
      layout: {
        body: ['What stage of menopause are you in?'],
        choices: ['Peri-menopause', 'Menopause', 'Post-menopause', 'I’m not sure', NoMenopausal],
      },
      nextScreen: {
        [NoMenopausal]: MenoScreenName.NotMenopausal,
        fallback: MenoScreenName.MenopauseHormoneLevel,
      },
    },
    {
      screenName: MenoScreenName.NotMenopausal,
      type: ScreenType.Presentation,
      layout: {
        primaryButtonTitle: 'Makes sense',
        content: [
          {
            type: 'text',
            content:
              'Evia was designed to help women manage their hot flushes during diagnosed menopause.',
          },
          {
            type: 'text',
            content:
              'This program is not suitable for people experiencing hot flushes due to other health problems, such as diabetes or hyperthyroidism.',
          },
          {
            type: 'text',
            content:
              'If you need help managing your symptoms, make an appointment with your doctor.',
          },
        ],
      },
      nextScreen: MenoScreenName.MenopauseHormoneLevel,
    },
    {
      screenName: MenoScreenName.MenopauseHormoneLevel,
      type: ScreenType.Presentation,
      layout: {
        primaryButtonTitle: 'Makes sense',
        content: [
          {
            type: 'text',
            content: 'Your hormone levels will fluctuate throughout each stage of menopause.',
          },
          {
            type: 'image',
            src: images.hormoneLevelGraph,
          },
          {
            type: 'text',
            content:
              'Most symptoms (like hot flushes, night sweats, insomnia, anxiety and depression) happen due to these hormonal changes.',
          },
        ],
      },
      nextScreen: MenoScreenName.HotFlash,
    },
    {
      screenName: MenoScreenName.HotFlash,
      type: ScreenType.SingleSelect,
      layout: {
        body: ['How many hot flushes do you experience each day, on average?'],
        choices: ['0', '1', '2', '3', '4', '5+'],
      },
      nextScreen: {
        0: MenoScreenName.SleepDifficulty,
        fallback: MenoScreenName.HotFlashIntensity,
      },
    },
    {
      screenName: MenoScreenName.HotFlashIntensity,
      type: ScreenType.SingleSelect,
      layout: {
        body: ['How severe are your hot flushes?'],
        choices: ['Very Mild', 'Mild', 'Moderate', 'Severe', 'Very Severe'],
      },
      nextScreen: MenoScreenName.BrainSafeRange,
    },
    {
      screenName: MenoScreenName.BrainSafeRange,
      type: ScreenType.Presentation,
      layout: {
        primaryButtonTitle: 'Makes sense',
        content: [
          {
            type: 'text',
            content: 'While hot flushes feel physical, they actually start in your brain.',
          },
          {
            type: 'text',
            content: 'Your brain recognizes a range of temperatures as “safe”.',
          },
          {
            type: 'text',
            content: 'But as your hormones fluctuate during menopause, this safe range can change.',
          },
          {
            type: 'image',
            src: images.safeTemperatureGraph,
            fullWidth: true,
          },
          {
            type: 'text',
            content:
              'When this happens, slight changes in temperature (like a hotter than usual day) can cause your brain to think it’s reached an unsafe temperature.',
          },
        ],
      },
      nextScreen: MenoScreenName.BrainPhysicalReaction,
    },
    {
      screenName: MenoScreenName.BrainPhysicalReaction,
      type: ScreenType.Presentation,
      layout: {
        primaryButtonTitle: 'Makes sense',
        content: [
          {
            type: 'text',
            content:
              'If your brain thinks you’ve reached an unsafe temperature, it will start a physical reaction to cool you down.',
          },
          {
            type: 'text',
            content:
              'Your blood vessels will dilate (giving you a flushed feeling), your blood will move faster around your body (increasing your heart rate), and the heat will evaporate from your skin (by sweating). This is what we call a hot flush.',
          },
          {
            type: 'image',
            src: images.menoPhysicalReaction,
            fullWidth: true,
          },
        ],
      },
      nextScreen: MenoScreenName.HotFlashSymptoms,
    },
    {
      screenName: MenoScreenName.HotFlashSymptoms,
      type: ScreenType.MultiSelect,
      layout: {
        body: ['What do your hot flushes feel like?', 'Select all that apply.'],
        primaryButtonTitle: NEXT_BUTTON_TITLE,
        choices: [
          'Sudden feeling of warmth',
          'Perspiration',
          'Flushed appearance',
          'Rapid heartbeat',
          'Anxiety/An aura',
          'Dizziness/Weakness',
          'Nausea',
          'Other',
        ],
      },
      nextScreen: MenoScreenName.SleepDifficulty,
    },
    {
      screenName: MenoScreenName.SleepDifficulty,
      type: ScreenType.SingleSelect,
      layout: {
        body: [
          'Many women also struggle with sleep during menopause. ',
          'Do you find it difficult to fall asleep each night?',
        ],
        choices: ['No', 'A little bit', 'Yes'],
      },
      nextScreen: MenoScreenName.NightSweatsHowMany,
    },
    {
      screenName: MenoScreenName.NightSweatsHowMany,
      type: ScreenType.SingleSelect,
      layout: {
        body: ['How many night sweats do you experience each night, on average?'],
        choices: ['0', '1', '2', '3', '4', '5+'],
      },
      nextScreen: {
        0: MenoScreenName.SleepQuality,
        fallback: MenoScreenName.SleepDisruption,
      },
    },
    {
      screenName: MenoScreenName.SleepDisruption,
      type: ScreenType.SingleSelect,
      layout: {
        body: ['For how long have night sweats disrupted your sleep?'],
        choices: ['Less than 1 month', '1-6 months', '6-12 months', 'More than 12 months'],
      },
      nextScreen: MenoScreenName.SleepQuality,
    },
    {
      screenName: MenoScreenName.SleepQuality,
      type: ScreenType.SingleSelect,
      layout: {
        body: ['How would you rate the quality of your sleep?'],
        choices: ['Terrible', 'Fair', 'Good', 'Excellent'],
      },
      nextScreen: MenoScreenName.Hypnothalamus,
    },
    {
      screenName: MenoScreenName.Hypnothalamus,
      type: ScreenType.Presentation,
      layout: {
        primaryButtonTitle: 'Makes sense',
        content: [
          {
            type: 'text',
            content:
              'Hormonal changes during menopause can impact part of your brain called the hypothalamus.',
          },
          {
            type: 'text',
            content:
              'Your hypothalamus is responsible for your body’s temperature control, and can wake you up in the middle of the night with sweats.',
          },
          {
            type: 'text',
            content: 'The hypothalamus has also been linked to general insomnia and sleeplessness.',
          },
          {
            type: 'image',
            src: images.hypothalamus,
          },
        ],
      },
      nextScreen: MenoScreenName.Anxiety,
    },
    {
      screenName: MenoScreenName.Anxiety,
      type: ScreenType.SingleSelect,
      layout: {
        body: [
          'Some women also struggle with mental health during menopause.',
          'How often do you feel anxious or depressed?',
        ],
        choices: ['Never', 'Sometimes', 'Often', 'Constantly'],
      },
      nextScreen: {
        Never: MenoScreenName.BrainAdapt,
        fallback: MenoScreenName.StruggleWork,
      },
    },
    {
      screenName: MenoScreenName.StruggleWork,
      type: ScreenType.SingleSelect,
      layout: {
        body: [
          'Many women find that anxiety and depression impacts their performance at work.',
          'Have you struggled with this?',
        ],
        choices: ['Yes', 'A little bit', 'No', 'I’m not working'],
      },
      nextScreen: MenoScreenName.StruggleRelationship,
    },
    {
      screenName: MenoScreenName.StruggleRelationship,
      type: ScreenType.SingleSelect,
      layout: {
        body: [
          'Anxiety and depression can also cause problems in romantic and intimate relationships.',
          'Is this something you’ve experienced?',
        ],
        choices: ['Yes', 'A little bit', 'No', 'Prefer not to say'],
      },
      nextScreen: MenoScreenName.BrainAdapt,
    },
    {
      screenName: MenoScreenName.BrainAdapt,
      type: ScreenType.Presentation,
      layout: {
        primaryButtonTitle: 'Okay',
        content: [
          {
            type: 'text',
            content:
              'While hormonal changes are responsible for the symptoms you’re experiencing, it’s your brain that’s triggering them.',
          },
          {
            type: 'text',
            content: 'The good news is, your brain will eventually learn to adapt.',
          },
          {
            type: 'text',
            content: 'This is why menopausal symptoms will eventually stop on their own.',
          },
          {
            type: 'text',
            content: 'But without help, it can take years.',
          },
          {
            type: 'text',
            content:
              'A recent study found that menopause symptoms can last for more than 10 years\u00B9.',
          },
        ],
        footnotes: [
          'Freeman, E.W., Sammel, M.D., Lin, H., Liu, Z. and Gracia, C.R. (2011). Duration of Menopausal Hot Flushes and Associated Risk Factors. Obstetrics and gynecology, [online] 117(5), pp.1095–1104. doi:https://doi.org/10.1097/AOG.0b013e318214f0de.',
        ],
      },
      nextScreen: SharedScreenName.HowLong,
    },
    {
      screenName: SharedScreenName.HowLong,
      type: ScreenType.SingleSelect,
      layout: {
        body: ['How long have you been experiencing menopause symptoms?'],
        choices: [
          '0 - 6 months',
          '6 - 12 months',
          '1 - 2 years',
          '2 - 3 years',
          '3 - 5 years',
          '5 - 10 years',
          '10+ years',
        ],
      },
      nextScreen: MenoScreenName.EviaSocialProof,
    },
    {
      screenName: MenoScreenName.EviaSocialProof,
      type: ScreenType.Presentation,
      layout: {
        primaryButtonTitle: 'Sounds good',
        content: [
          {
            type: 'text',
            content:
              'Many women decide to use a tool to help them manage symptoms, rather than suffer for years without support.',
          },
          {
            type: 'text',
            content:
              'In fact, over 90% of women in our community had tried other options before they found Evia. You’re in good company!',
          },
          {
            type: 'text',
            content: 'Next, we’ll ask a little about what you’ve already tried.',
          },
        ],
      },
      nextScreen: SharedScreenName.Doctor,
    },
    {
      screenName: SharedScreenName.Doctor,
      type: ScreenType.SingleSelect,
      layout: {
        body: ['Were you referred to Evia by a healthcare professional?'],
        choices: ['Yes', 'No'],
      },
      nextScreen: {
        Yes: MenoScreenName.Hormones,
        No: SharedScreenName.NoDoctorReferral,
        fallback: MenoScreenName.Hormones,
      },
    },
    {
      screenName: SharedScreenName.NoDoctorReferral,
      type: ScreenType.Presentation,
      layout: {
        content: [
          { type: 'heading', content: 'That’s okay!' },
          { type: 'image', src: images.doctor, altText: 'doctor' },
          {
            type: 'coloredBox',
            body: 'No problem! Just make sure to check with your healthcare practitioner before making any medical decisions, like self-diagnosing menopause.',
          },
        ],
        primaryButtonTitle: 'Sounds good',
      },
      nextScreen: SharedScreenName.Attribution,
    },
    {
      screenName: SharedScreenName.Attribution,
      type: ScreenType.SingleSelect,
      layout: {
        body: ['How did you hear about Evia?'],
        choices: [
          'Facebook',
          'TikTok',
          'App Store',
          'Influencer',
          'Friends or family',
          'Instagram',
          'Search engine (Google, Bing)',
          'Blog post or news article',
          'Magazine or print',
          'Support group',
          'Other',
        ],
        randomizeChoices: true,
      },
      nextScreen: MenoScreenName.Hormones,
    },
    {
      screenName: MenoScreenName.Hormones,
      type: ScreenType.SingleSelect,
      layout: {
        body: ['Are you currently using hormone replacement therapy (HRT)?'],
        choices: ['Yes', 'No'],
      },
      nextScreen: {
        Yes: MenoScreenName.ChangeHrt,
        No: MenoScreenName.NaturalToolsTried,
        fallback: MenoScreenName.NaturalToolsTried,
      },
    },
    {
      screenName: MenoScreenName.ChangeHrt,
      type: ScreenType.SingleSelect,
      layout: {
        body: ['Are you planning to change your HRT dose in the future?'],
        choices: ['No', 'Yes – stop taking', 'Yes – decrease dose', 'Yes – increase dose'],
      },
      nextScreen: MenoScreenName.HrtSideEffects,
    },
    {
      screenName: MenoScreenName.HrtSideEffects,
      type: ScreenType.MultiSelect,
      layout: {
        body: ['Do you experience any of these common HRT side effects?', 'Select all that apply.'],
        primaryButtonTitle: NEXT_BUTTON_TITLE,
        randomizeChoices: true,
        choices: [
          'Bloating',
          'Breast tenderness',
          'Swelling',
          'Feeling sick',
          'Leg cramps',
          'Headaches',
          'Indigestion',
          'Vaginal bleeding',
        ],
      },
      nextScreen: MenoScreenName.NaturalToolsTried,
    },
    {
      screenName: MenoScreenName.NaturalToolsTried,
      type: ScreenType.MultiSelect,
      layout: {
        body: ['What natural tools have you tried?', 'Select all that apply.'],
        primaryButtonTitle: NEXT_BUTTON_TITLE,
        choices: [
          'Exercise changes',
          'Diet changes',
          'Cooling tools (spray bottles, fans)',
          'Red clover',
          'Black cohosh',
          'Acupuncture',
          'None',
          'Other',
        ],
      },
      nextScreen: {
        None: MenoScreenName.ToolsIneffectivenessReason,
        fallback: MenoScreenName.NaturalToolsEfectiveness,
      },
    },
    {
      screenName: MenoScreenName.NaturalToolsEfectiveness,
      type: ScreenType.SingleSelect,
      layout: {
        body: ['How effective have you found the natural tools you’ve tried?'],
        choices: ['Not effective', 'Somewhat effective', 'Quite effective'],
      },
      nextScreen: MenoScreenName.ToolsIneffectivenessReason,
    },
    {
      screenName: MenoScreenName.ToolsIneffectivenessReason,
      type: ScreenType.Presentation,
      layout: {
        primaryButtonTitle: 'Makes sense',
        content: [
          {
            type: 'text',
            content:
              'Many natural tools aren’t effective because they don’t target what’s actually causing your symptoms.',
          },
          {
            type: 'text',
            content: 'Symptoms are triggered by your brain in response to hormonal changes.',
          },
          {
            type: 'text',
            content:
              'Unless a tool artificially increases your hormones or helps your brain adapt, it’s unlikely to be effective long term.',
          },
        ],
      },
      nextScreen: MenoScreenName.NonHormonalTool,
    },
    {
      screenName: MenoScreenName.NonHormonalTool,
      type: ScreenType.SingleSelect,
      layout: {
        body: [
          'Many women don’t want to artificially increase their hormone levels.',
          'For you, how important is it to use a non-hormonal tool?',
        ],
        choices: ['Not important', 'Somewhat important', 'Extremely important'],
      },
      nextScreen: MenoScreenName.EviaHypnotherapy,
    },
    {
      screenName: MenoScreenName.EviaHypnotherapy,
      type: ScreenType.Presentation,
      layout: {
        primaryButtonTitle: 'Makes sense',
        content: [
          {
            type: 'text',
            content:
              'Evia is non-hormonal, but still targets the underlying cause of your symptoms: your brain.',
          },
          {
            type: 'text',
            content: 'Evia uses science-backed hypnotherapy to help you teach your brain to adapt.',
          },
          {
            type: 'text',
            content: 'It’s like speeding up the natural process.',
          },
          {
            type: 'text',
            content:
              'Menopause symptoms can last years, but with hypnotherapy, you can teach your brain to adapt in just a few weeks.',
          },
          {
            type: 'image',
            src: images.franTestimonial,
          },
        ],
      },
      nextScreen: MenoScreenName.HotFlashesHypnotherapy,
    },
    {
      screenName: MenoScreenName.HotFlashesHypnotherapy,
      type: ScreenType.SingleSelect,
      layout: {
        body: ['Have you heard of hypnotherapy for menopause?'],
        choices: ['Yes', 'A little bit', 'No'],
      },
      nextScreen: MenoScreenName.HotFlashManagement,
    },
    {
      screenName: MenoScreenName.HotFlashManagement,
      type: ScreenType.Presentation,
      layout: {
        primaryButtonTitle: 'Makes sense',
        content: [
          {
            type: 'text',
            content: 'Hypnotherapy is a clinically proven technique for managing menopause.',
          },
          {
            type: 'text',
            content:
              'If you’re suffering with hot flushes, you can use hypnotherapy to teach your brain to better respond to temperature changes.',
          },
          {
            type: 'text',
            content:
              'This can help you control how often hot flushes are triggered, and how severe they feel.',
          },
          {
            type: 'text',
            content:
              'Several studies have proven that hypnotherapy can help manage hot flushes in a matter of weeks\u00B9.',
          },
          {
            type: 'image',
            src: images.hotFlashManagementGraph,
          },
        ],
        footnotes: [
          'Elkins, G.R., Fisher, W.I., Johnson, A.K., Carpenter, J.S. and Keith, T.Z. (2012). Clinical hypnosis in the treatment of postmenopausal hot flashes. Menopause: The Journal of The North American Menopause Society, p.1. doi:https://doi.org/10.1097/gme.0b013e31826ce3ed.',
        ],
      },
      nextScreen: MenoScreenName.HypnotherapySleep,
    },
    {
      screenName: MenoScreenName.HypnotherapySleep,
      type: ScreenType.Presentation,
      layout: {
        primaryButtonTitle: 'Makes sense',
        content: [
          {
            type: 'text',
            content: 'Hypnotherapy can also help you sleep better.',
          },
          {
            type: 'image',
            src: images.michelleTestimonial,
          },
          {
            type: 'text',
            content:
              'With hypnotherapy, you can target your hypothalamus – the part of your brain that regulates temperature, triggers night sweats, and is linked to insomnia.',
          },
          {
            type: 'text',
            content:
              'A recent clinical trial proved that hypnotherapy can significantly improve both the quality and duration of your sleep\u00B9.',
          },
          {
            type: 'image',
            src: images.hypnoTrialFindings,
          },
        ],
        footnotes: [
          'Otte, J.L., Carpenter, J.S., Roberts, L. and Elkins, G.R. (2020). Self-Hypnosis for Sleep Disturbances in Menopausal Women. Journal of Women’s Health, 29(3), pp.461–463. doi:https://doi.org/10.1089/jwh.2020.8327.',
        ],
      },
      nextScreen: MenoScreenName.HypnotherapyMentalHealth,
    },
    {
      screenName: MenoScreenName.HypnotherapyMentalHealth,
      type: ScreenType.Presentation,
      layout: {
        primaryButtonTitle: 'Makes sense',
        content: [
          {
            type: 'text',
            content:
              'Finally, multiple studies have shown that hypnotherapy can help manage mental health during menopause.',
          },
          {
            type: 'text',
            content:
              'For example, in this 2020 study, participants who completed a five-week hypnotherapy program significantly reduced their reported anxiety levels by the end of it\u00B9.',
          },
          {
            type: 'image',
            src: images.hypnoMentalHealthGraph,
          },
        ],
        footnotes: [
          'Elkins, G., Marcus, J., Stearns, V., Perfect, M., Rajab, M.H., Ruud, C., Palamara, L. and Keith, T. (2008). Randomized Trial of a Hypnosis Intervention for Treatment of Hot Flashes Among Breast Cancer Survivors. Journal of Clinical Oncology, 26(31), pp.5022–5026. doi:https://doi.org/10.1200/jco.2008.16.6389.',
        ],
      },
      nextScreen: MenoScreenName.HypnotherapyTried,
    },
    {
      screenName: MenoScreenName.HypnotherapyTried,
      type: ScreenType.SingleSelect,
      layout: {
        body: ['Have you tried any hypnotherapy before?'],
        choices: ['Yes', 'No'],
      },
      nextScreen: MenoScreenName.HypnotherapyEstablishedTechnique,
    },
    {
      screenName: MenoScreenName.HypnotherapyEstablishedTechnique,
      type: ScreenType.Presentation,
      layout: {
        primaryButtonTitle: 'Makes sense',
        content: [
          {
            type: 'text',
            content:
              'Hypnotherapy has been an established and trusted menopause management technique for years.',
          },
          {
            type: 'text',
            content:
              'It’s recommended by both the Royal Australian and New Zealand College of Obstetricians and Gynecologists and The North American Menopause Society\u00B9 \u00B2.',
          },
          {
            type: 'image',
            src: logos.ranzcogAndNams,
          },
          {
            type: 'text',
            content:
              'Unfortunately, long waitlists and expensive in-person consultations have made it difficult for many women to benefit from it. Until now.',
          },
        ],
        footnotes: [
          'The North American Menopause Society (2015). Nonhormonal management of menopause-associated vasomotor symptoms. Menopause, 22(11), pp.1155–1174. doi:https://doi.org/10.1097/gme.0000000000000546.',
          'The 2023 nonhormone therapy position statement of The North American Menopause Society. (2023). Menopause (New York, N.Y.), [online] 30(6), pp.573–590. doi:https://doi.org/10.1097/GME.0000000000002200.',
        ],
      },
      nextScreen: MenoScreenName.DrElkins,
    },
    {
      screenName: MenoScreenName.DrElkins,
      type: ScreenType.Presentation,
      layout: {
        primaryButtonTitle: 'Makes sense',
        content: [
          {
            type: 'text',
            content:
              'We partnered with world leading researcher, Dr. Elkins, to create hypnotherapy for menopause that you can experience from the comfort of home.',
          },
          {
            type: 'image',
            src: images.drElkinsCredentialsVertical,
          },
          {
            type: 'text',
            content:
              'Dr. Elkins is a respected leader in both the menopause and clinical hypnotherapy research fields.',
          },
          {
            type: 'text',
            content:
              'It’s important to us that Evia is backed by experts, so we only partner with the best.',
          },
        ],
      },
      nextScreen: MenoScreenName.UserInfoRecap,
    },
    {
      screenName: MenoScreenName.UserInfoRecap,
      type: ScreenType.UserInfoRecap,
      layout: {
        title: ['Could Evia’s hypnotherapy work for you?'],
        body: [
          'Here’s a summary of the information you’ve shared. Please confirm it’s correct so we can finalize your assessment.',
        ],
        primaryButtonTitle: 'Yes, it’s correct',
      },
      nextScreen: SharedScreenName.Auth,
    },
    {
      screenName: SharedScreenName.Auth,
      type: ScreenType.Auth,
      layout: {
        ...authLayout,
      },
      nextScreen: {
        loginAndAlreadyPaid: SharedScreenName.AlreadyPaidDownloadApp,
        login: SharedScreenName.Results,
        register: SharedScreenName.LoadingResults,
        fallback: SharedScreenName.Results,
      },
      nextScreenSelector: getAuthMethodUsed,
    },
    {
      screenName: SharedScreenName.AlreadyPaidDownloadApp,
      type: ScreenType.DownloadApp,
      layout: {
        // Cannot call `translate()` from config without calling `Localization.initialize()` in the same file.
        // Second line of Title coming from translation JSON to avoid 'missing translation' bug.
        title: ['You already have access!'],
        body: ['Use the same email and password you signed up with:'],
        alreadyPaid: true,
      },
    },
    {
      screenName: SharedScreenName.LoadingResults,
      type: ScreenType.LoadingResults,
      layout: {
        reviewer: {
          name: 'Fran F.',
          image: images.fran,
          testimonial: [
            '“What a great app! The best part about Evia’s hypnosis is it can be used for anything that’s bothersome whether it’s hot flushes, night sweats, or anxiety in general.”',
          ],
          date: 'United States, Jan 31, 2022',
        },
      },
      nextScreen: SharedScreenName.Results,
    },
    {
      screenName: SharedScreenName.Results,
      type: ScreenType.Results,
      layout: {
        title: 'The results are in! ',
        summaryBoxHeading: 'You’re a great candidate for Evia.',
        summaryBoxBody: 'The program is designed to help manage the symptoms you’re experiencing',
        body: [
          'We’ve helped women like you manage hot flushes, insomnia, night sweats, anxiety and depression with our five-week program.',
          'Many of them reported feeling better within the first few days.',
        ],
        primaryButtonTitle: 'See Your Program',
      },
      nextScreen: SharedScreenName.ProgramStructure,
    },
    {
      screenName: SharedScreenName.ProgramStructure,
      type: ScreenType.ProgramStructure,
      layout: {
        title: 'Here’s your\nEvia program',
        coreProgramWeekTitles: [
          'Starting your Journey with Evia',
          'Hot Flushes & You',
          'Understanding Hypnotherapy',
          'Better Health during Menopause',
          'Personalizing Your Practice',
        ],
        postProgramTitle: 'Post 5-week program',
        postProgramContent: 'Unlock your ongoing Menopause management plan',
        primaryButtonTitle: 'Okay!',
      },
      nextScreen: SharedScreenName.ProgramBenefits,
    },
    {
      screenName: SharedScreenName.ProgramBenefits,
      type: ScreenType.ProgramBenefits,
      layout: {
        title: 'You’ll start with a\n5-week program',
        content: [
          {
            type: 'text',
            content:
              'Through the Evia program, you’ll learn how to help your brain adapt to changing hormone levels.',
          },
          { type: 'image', src: images.menoProgram, fullWidth: true },
          {
            type: 'text',
            content:
              'As your brain adapts, you’ll be able to manage symptoms like hot flushes, night sweats, insomnia, anxiety and depression – without hormones.',
          },
          {
            type: 'text',
            content: 'Many women start to see changes within the first few days.',
          },
        ],
        primaryButtonTitle: NEXT_BUTTON_TITLE,
      },
      nextScreen: SharedScreenName.CoreProgramExperience,
    },
    {
      screenName: SharedScreenName.CoreProgramExperience,
      type: ScreenType.Screenshot,
      layout: {
        image: images.menoScreenshot,
        imageAltText: 'Evia core program experience',
        listTitle: 'During the first 5 weeks you’ll:',
        listItems: [
          { image: icons.volumeBlue, desc: 'Listen to daily hypnotherapy' },
          { image: icons.booksBlue, desc: 'Read menopause psychoeducation' },
          { image: icons.sleepBlue, desc: 'Use optional sleep sessions' },
          { image: icons.graphBlue, desc: 'Track your progress' },
        ],
        primaryButtonTitle: NEXT_BUTTON_TITLE,
      },
      nextScreen: SharedScreenName.ProgramSupport,
    },
    {
      screenName: SharedScreenName.ProgramSupport,
      type: ScreenType.Presentation,
      layout: {
        title: 'Unlock long-term\nmenopause support',
        content: [
          {
            type: 'text',
            content: `After the first five weeks of Evia, you’ll have full access to our ongoing support program to 
							help you maintain and improve your learnings long-term.`,
          },
          { type: 'image', src: images.menoOngoingSupport, fullWidth: true },
          {
            type: 'text',
            content: `At this stage of your journey, you will unlock your customizable menopause support tools, 
							have access to new hypnotherapy sessions as they’re released, 
							and access continued symptom tracking and sleep tools.`,
          },
        ],
        primaryButtonTitle: NEXT_BUTTON_TITLE,
      },
      nextScreen: SharedScreenName.MaintenanceExperience,
    },
    {
      screenName: SharedScreenName.MaintenanceExperience,
      type: ScreenType.Screenshot,
      layout: {
        image: images.menoMaintenanceScreenshot,
        imageAltText: 'Evia maintenance program experience',
        listTitle: 'After the first 5 weeks you’ll:',
        listItems: [
          { image: icons.graphBlue, desc: 'Maintain and/or improve results' },
          { image: icons.wheelBlue, desc: 'Customize your program' },
          { image: icons.lockBlueUnlocked, desc: 'Access all new sessions' },
          { image: icons.refreshBlue, desc: 'Access ongoing support' },
        ],
        primaryButtonTitle: NEXT_BUTTON_TITLE,
      },
      nextScreen: SharedScreenName.TrialExplanation,
    },
    {
      screenName: SharedScreenName.TrialExplanation,
      type: ScreenType.TrialExplanation,
      layout: {
        title: 'Try your first 7 days free',
        body: [
          'It’s important to us that Evia is a good fit for you, so we offer the first week free of charge.',
          'If you decide it’s not right for you, we’ve made it simple to cancel.',
        ],
        image: images.menoProgram,
        imageAltText: 'Evia payment timeline',
        primaryButtonTitle: 'Set up free trial',
      },
      nextScreen: SharedScreenName.Payment,
    },
    {
      screenName: SharedScreenName.Payment,
      type: ScreenType.Payment,
      showHeaderBar: false,
      nextScreen: {
        newUser: SharedScreenName.FinishAccount,
        existingUser: SharedScreenName.DownloadApp,
        fallback: SharedScreenName.DownloadApp,
      },
      nextScreenSelector: getPaymentRoute,
    },
    {
      screenName: SharedScreenName.FinishAccount,
      type: ScreenType.FinishAccount,
      layout: {
        // Cannot call `translate()` from config without calling `Localization.initialize()` in the same file.
        // Title and info coming from translation JSON to avoid 'missing translation' bug.
        subtitle: 'Fill in these last few details and you’re ready to go!',
        primaryButtonTitle: 'Continue',
      },
      nextScreen: SharedScreenName.DownloadApp,
    },
    {
      screenName: SharedScreenName.DownloadApp,
      type: ScreenType.DownloadApp,
      layout: {
        title: ['You’re all set!', 'Access your program now'],
        // Cannot call `translate()` from config without calling `Localization.initialize()` in the same file.
        // body coming from translation JSON to avoid 'missing translation' bug.
        alreadyPaid: false,
      },
    },
  ],
};
